import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { notify } from '@app/notifications/notifyBridge';
import { parseApiError } from '@services/apiError';

/**
 * Shared TanStack Query client for the whole app.
 *
 * ONE client lives at the app root and is passed via <QueryClientProvider>
 * in main.jsx. Every useQuery / useMutation hook reads from it, so cache
 * hits are shared across pages / components. Do not create a second one.
 *
 * Defaults chosen for THIS app:
 *
 *   staleTime: 60_000 (1 min)
 *     Reservations, groups, and availability data don't change second-
 *     by-second on the server. A one-minute freshness window is a big
 *     win for perceived speed (revisiting a page in the same session is
 *     a cache hit) without letting genuinely stale UI linger.
 *
 *   gcTime: 5 * 60_000 (5 min)
 *     How long inactive queries linger in the cache before eviction.
 *     Five minutes matches a typical user session hop pattern
 *     (browse → detail → back → different detail) so returning to a
 *     page still hits cache.
 *
 *   retry: 1
 *     Network glitches deserve one retry; anything beyond that is
 *     probably a real error the user should see. Note it retries EVERY
 *     failure, 4xx included — this said 4xx were surfaced immediately,
 *     and they were not. A read that wants that passes
 *     `retry: retryTransientOnce` (below).
 *
 *   refetchOnWindowFocus: false
 *     The default (true) is nice for dashboards but distracting for
 *     forms mid-edit. Individual hooks can opt in per resource.
 *
 * Every server-state discipline in this app is documented in
 * docs/frontend/server-state-migration.md — read that FIRST before
 * changing anything here.
 */
/**
 * Error display, decided once for the whole app.
 *
 * The axios interceptor in http.js used to toast every 401/403/5xx. It cannot
 * tell a background list refresh from a payment submit — every request looks
 * identical at that layer — so it announced both the same way, and pages that
 * ALSO rendered the error inline ended up showing one failure twice.
 *
 * React Query already models the distinction that matters:
 *
 *   query    — data loading on its own. The user pressed nothing and there is
 *              no form on screen to attach a message to, so a toast is the
 *              place — unless every page reading it replaces itself with
 *              the failure (see announceQueryError).
 *   mutation — the user pressed something and is waiting. The error belongs
 *              beside that control, which only the calling component can
 *              place. It stays quiet here unless nobody claimed it.
 *
 * `mutation.options.onError` answers "did the caller take ownership?" from
 * information the library already holds — no per-request flag, and nothing
 * for a page to remember.
 *
 * Covers everything routed through useQuery / useMutation. Calls still made
 * with a raw try/catch announce through notifyApiError instead; moving those
 * onto useMutation is what retires that second path.
 *
 * The handlers are exported so queryClient.test.jsx runs these, not a copy.
 */

/**
 * A failed read toasts — unless the read declares
 * `meta: { errorAnnouncedByCaller: true }`.
 *
 * Only a read whose EVERY consumer replaces the page with the failure may
 * declare it; replacing the page is one of the places an inline message is
 * guaranteed to be seen. The hold read (useReservationHold) is one: its three
 * pages swap in the expired / couldn't-load card. Before it declared this, an
 * expired hold put "Your room selection expired…" in the snackbar AND the card
 * at once. A read that a page shows nowhere must never declare it — that is
 * the silent failure, worse than the duplicate.
 */
export function announceQueryError(error, query) {
  if (query?.meta?.errorAnnouncedByCaller) return;
  notify.error(parseApiError(error).message);
}

/**
 * Retry a read once for a transient failure — a 5xx or no response — but not
 * a 4xx: the server has answered, and asking again gets the same answer. An
 * expired hold's 410 was asked for twice, and the page waited out the retry
 * before saying so.
 *
 * Opt-in per query. The default below is still `retry: 1` for every failure,
 * whatever its comment says about 4xx.
 */
export function retryTransientOnce(failureCount, error) {
  const status = error?.response?.status ?? null;
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

export function announceMutationError(error, _variables, _context, mutation) {
  // ── The claim ──────────────────────────────────────────────────────
  // `meta.errorAnnouncedByCaller` says the calling code announces this
  // failure itself, so a backstop toast would be a second announcement of
  // one event.
  //
  // Named for the CONTRACT, not a surface. A caller may announce through
  // the snackbar (the usual case — a page whose submit path mixes
  // mutations with raw calls funnels everything through one catch) or
  // inline inside a dialog, where the message is guaranteed visible. What
  // the cache needs to know is only "has someone taken responsibility".
  //
  // It is declared as meta rather than inferred from a callback because
  // the callback version was wrong in both directions:
  //
  //   - A hook with onError for an unrelated reason counted as claiming.
  //     useModifyReservation declares one purely to refetch after a
  //     VERSION_CONFLICT; its failures are rendered inline by the two
  //     hooks that call it, so the behaviour was right — by luck. Delete
  //     the cache line and modify errors start appearing twice.
  //   - A hook that genuinely claimed had to write `onError: () => {}`,
  //     an empty function whose only job is to exist. It reads as dead
  //     code, and three group mutations were missing it: on 2026-09-11 a
  //     VCC refusal showed as a toast AND a page alert at once.
  //
  // meta says what is meant. A reader does not have to know that an empty
  // callback is load-bearing.
  if (mutation.meta?.errorAnnouncedByCaller) return;

  // ── Legacy claim, still honoured ───────────────────────────────────
  // Twenty of the app's twenty-four mutations still signal this way.
  // Dropping it here would silently turn each of them into a duplicate
  // toast, so it stays until each one has been checked and moved — the
  // check being "does a caller actually render this?", which cannot be
  // answered from the hook. errorOwnership.test.js lists what is left.
  if (mutation.options.onError) return;

  // Nobody claimed it. A toast is a poor place for a failed save, but a
  // silent one is worse — this is the backstop, not the intended path.
  notify.error(parseApiError(error).message);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: announceQueryError }),
  mutationCache: new MutationCache({ onError: announceMutationError }),

  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Mutations don't retry — a duplicate write is worse than a
      // visible error. Explicit .retry() call sites can opt in.
      retry: 0,
    },
  },
});
